import { getLogger, type Logger } from "@expert/logging";

export class ConnectCallbackTimeoutError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "TimeoutError";
    }
}

interface Callbacks<T> {
    readonly success: connect.SuccessFailCallback<[T]>;
    readonly failure?: connect.SuccessFailCallback<[string]>;
}

interface WithCallbacksOptions {
    logger: Logger;
    action: string;
    timeout?: number;
    startTimestamp?: number;
    logContext?: Record<string, unknown>;
}

const defaultLogger = getLogger({ module: "withCallbacks" });

/**
 * Convenience method to promisify an `amazon-connect-streams` callback-based method.
 *
 * @example
 * ```ts
 * await withCallbacks((callbacks) => contact.accept(callbacks));
 * ```
 *
 * It can also be used for value-returning methods by explicitly specifying its type.
 *
 * @template T the type the returned `Promise` resolves to
 *
 * @example
 * ```ts
 * const { endpoints, addresses } = await withCallbacks<connect.GetEndpointsResult>((callbacks) =>
 *   agent.getEndpoints(agent.getAllQueueARNs(), callbacks),
 * );
 * ```
 */
export default function withCallbacks<T = void>(
    callback: (callbacks: Callbacks<T>) => void,
    { logger, action, startTimestamp = performance.now(), logContext, timeout }: Partial<WithCallbacksOptions> = {
        logger: defaultLogger,
    },
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        if (timeout) {
            setTimeout(() => {
                return reject(new ConnectCallbackTimeoutError("withCallbacks timed out"));
            }, timeout);
        }

        callback({
            success: (value: T) => {
                const latency = startTimestamp ? performance.now() - startTimestamp : undefined;
                logger?.info({ action, latency, ...logContext }, "Amazon Connect streams API invocation succeeded");
                return resolve(value);
            },
            failure: (reason) => {
                const latency = startTimestamp ? performance.now() - startTimestamp : undefined;
                logger?.warn(
                    { reason, action, latency, ...logContext },
                    "Amazon Connect streams API invocation failed",
                );
                return reject(new Error(reason));
            },
        });
    });
}
